import classNames from 'classnames';
import type { DeliveryTime } from '@hub/libs/constants';
import { DeliveryInfo } from '@hub/common/DeliveryInfo/DeliveryInfo';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { Button, TYPE } from '@hub/common/Button/Button';
import { useTranslation } from 'next-i18next';
import { BuyBoxModals } from '@hub/common/BuyBox/modals/BuyBoxModals';
import { Modal, ModalVariant } from '@hub/common/Modal/Modal';
import {
  ShoppingCartModalContent,
  ShoppingCartModalContentMode,
} from '@hub/common/BuyBox/modals/ShoppingCartModalContent';
import { useRouter } from 'next/router';
import { PriceScale } from '@hub/bearing-hub/ProductDetails/PriceScale/PriceScale';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { useAddToShoppingCart } from '@hub/bearing-hub/common/basket/useAddToShoppingCart';
import { ComplianceModal } from '@hub/bearing-hub/common/basket/ComplianceModal';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';

import { IbhEventCategory, TrackedIbhEventActions } from '@hub/bearing-hub/common/tracking.config';
import { useCopyLinkToClipboard } from '@hub/hooks/useCopyToClipboard';
import { useDebouncedCallback } from 'use-debounce';
import { useEffect, useState } from 'react';
import { useBearingArticlePriceScalesApi } from '@hub/hooks/useBearingArticlePriceScalesApi';
import type {
  IbhCatalogArticleResponseDto,
  IbhCustomArticleResponseDto,
  IbhIcnArticleResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { displayPrice } from '@hub/libs/product-price-utils';
import { MAX_PRICE_QUANTITY } from '@hub/bearing-hub/common/constants';
import { BasicNumberInput } from '@hub/common/BasicNumberInput/BasicNumberInput';

interface ProductDetailsBuyBoxProps {
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto | IbhIcnArticleResponseDto;
  quantity: number;
  onQuantityChange: (value: number) => void;
  setupCostIsLoading: boolean;
  articleIsCustom: boolean;
  session: Session;
  articleThumbnail?: string;
}

export const ProductDetailsBuyBox = ({
  articleData,
  quantity,
  onQuantityChange,
  setupCostIsLoading,
  articleIsCustom,
  session,
  articleThumbnail,
}: ProductDetailsBuyBoxProps) => {
  const { t, i18n } = useTranslation(['common', 'product/buyBox']);
  const router = useRouter();
  const { CustomEvent } = usePiwikPro();
  const { copyLinkToClipboard } = useCopyLinkToClipboard(
    t(`SHARE_LINK_TOAST_SUCCESS`, { ns: 'bearing-hub/bearingHub' })
  );
  const { articleNumber, setupCost, unitPrice, unitSystem, shape, dimensions, unitPriceForOne } = articleData;

  const {
    addToShoppingCart,
    addToBasketInProgress,
    setActiveModal,
    activeModal,
    pendingComplianceArticle,
    setPendingComplianceArticle,
    addPendingComplianceArticleToShoppingCart,
  } = useAddToShoppingCart({
    session,
    quantity,
    shape,
    setupCostIsLoading,
    i18n,
    complianceConfirmationEnabled: true,
  });

  const [currentQuantity, setCurrentQuantity] = useState(quantity);
  const [priceScaleQuantity, setPriceScaleQuantity] = useState<string | undefined>();
  const [currentQuantityLoaded, setCurrentQuantityLoaded] = useState(false);
  const { priceScales, fetchPriceScales } = useBearingArticlePriceScalesApi({
    articleNumber,
    isCustom: articleIsCustom,
    shape,
    quantity,
    unitSystem,
    dimensions,
    session,
  });
  const priceAvailable = unitPrice?.value > -1 && quantity < MAX_PRICE_QUANTITY;
  const icnPriceScales = (articleData as IbhIcnArticleResponseDto).priceScales;
  const onQuantityChangeDebounce = useDebouncedCallback((value: number) => {
    onQuantityChange(value);
  }, 500);

  const extractPriceScaleValues = () => {
    const scale = priceScales.map(priceScale => ({
      quantity: priceScale.quantityText.match(/\d+(,)?(.)?(\d+)?/g)?.[0],
      price: priceScale.priceText.match(/\d+(,)?(.)?(\d+)?/g)?.[0],
    }));

    const index = scale.findIndex(priceScale => Number(priceScale.quantity) > currentQuantity);
    const scaleQuantity = (index > 0 ? scale[index - 1] : scale[scale.length - 1]).quantity;
    setPriceScaleQuantity(scaleQuantity);
  };
  useEffect(() => {
    if (priceScales?.length > 0) {
      extractPriceScaleValues();
    }
  }, [currentQuantity, priceScales]);

  useEffect(() => {
    if (!currentQuantityLoaded && quantity !== currentQuantity) {
      setCurrentQuantity(quantity);
      setCurrentQuantityLoaded(true);
    }
  }, [quantity]);

  return (
    <div className="flex lg:justify-end">
      <div className={classNames('bg-gray-50', 'p-8', 'lg:w-3/4')} data-testid="buy-box-container">
        <div className="max-w-xs">
          <BasicNumberInput
            id="quantity-input"
            onlyInt
            placeholder={t('BUY_BOX.QUANTITY', { ns: 'product/buyBox' })}
            value={currentQuantity.toString()}
            label={t('BUY_BOX.QUANTITY', { ns: 'product/buyBox' })}
            validator={value => Number(value) > 0}
            onChange={value => {
              if (value !== undefined) {
                CustomEvent.trackEvent(IbhEventCategory, TrackedIbhEventActions.quantity, value.toString());
                onQuantityChangeDebounce(Number(value));
                setCurrentQuantity(Number(value));
              }
            }}
          />
        </div>

        <div className={articleIsCustom ? 'my-8' : 'mt-8'} data-testid="buy-box-prices-container">
          {setupCost?.value !== undefined && !Number.isNaN(setupCost.value) && setupCost.value > 0 ? (
            <span className="text-xs block">{t('BUY_BOX.PRICE', { ns: 'product/buyBox' })}: </span>
          ) : (
            <span className="text-xs block">{t('BUY_BOX.TOTAL_PRICE', { ns: 'product/buyBox' })}: </span>
          )}
          {!priceAvailable ? (
            <span data-testid="total-price-upon-request" className="text-4xl block">
              {t(`BUY_BOX.PRICE_ON_REQUEST`, { ns: 'product/buyBox' })}
            </span>
          ) : (
            <>
              <span
                data-testid="total-price"
                className={`text-4xl block ${setupCostIsLoading ? 'invisible' : 'visible'}`}
              >
                {displayPrice(unitPrice.value * quantity, unitPrice.unit, i18n.language)}
              </span>
              <span
                data-testid="unit-price"
                className={`text-xs block ${setupCostIsLoading ? 'invisible' : 'visible'}`}
              >
                {displayPrice(unitPrice.value, unitPrice.unit, i18n.language)} /{' '}
                {t(`HEADLINES.PIECE`, { ns: 'bearing-hub/bearingHub' })}
              </span>
              {setupCost?.value !== undefined && !Number.isNaN(setupCost.value) && setupCost.value > 0 && (
                <>
                  <p className="text-xxs mt-1 text-gray-300">
                    {t(`PRICE_PLUS_SETUP_COSTS`, {
                      price: `${displayPrice(setupCost.value, setupCost.unit, i18n.language)}`,
                      ns: 'bearing-hub/bearingHub',
                    })}
                  </p>
                  <span className="text-xs block mt-8">{t('BUY_BOX.TOTAL_PRICE', { ns: 'product/buyBox' })}: </span>
                  <span
                    data-testid="final-price"
                    className={`text-4xl block ${setupCostIsLoading ? 'invisible' : 'visible'}`}
                  >
                    {displayPrice(unitPrice.value * quantity + setupCost.value, setupCost.unit, i18n.language)}
                  </span>
                </>
              )}
            </>
          )}
          {priceScaleQuantity && (
            <p data-testid="price-explanation" className="text-xxs text-gray-300 mt-1">
              {t(`BUY_BOX.PRICE_EXPLANATION`, { quantity: priceScaleQuantity, ns: 'product/buyBox' })}
            </p>
          )}
        </div>
        {priceAvailable && (
          <>
            <p className="text-xxs text-gray-400 mt-1">
              {t(`PRICE_PLUS_TAX_AND_SHIPPING`, { ns: 'bearing-hub/bearingHub' })}
            </p>
            <div className="my-6">
              <p data-testid="unit-price-explanation" className="text-xs">
                {t(`BUY_BOX.UNIT_PRICE_EXPLANATION`, {
                  unitPrice: displayPrice(unitPriceForOne?.value, unitPriceForOne?.unit, i18n.language),
                  ns: 'product/buyBox',
                })}
              </p>
            </div>
          </>
        )}
        <div className="my-4">
          <PriceScale
            articleNumber={articleNumber}
            partsPriceScales={priceScales}
            icnPriceScales={icnPriceScales}
            fetchPriceScales={fetchPriceScales}
          />
        </div>
        <div className="text-[15px]">
          <DeliveryInfo
            deliveryTimeCode={articleData.deliveryTimeCode.toString() as DeliveryTime}
            deliveryTimeText={articleData.deliveryTimeText}
          />
        </div>
        {setupCost?.value !== undefined && !Number.isNaN(setupCost.value) && setupCost.value > 0 && (
          <div className="flex items-start my-6">
            <div className="rotate-180 mr-4">
              <Icon size={9} id={IconID.INFO_CIRCLE_MODERN} />
            </div>
            <p className="italic text-sm text-igus-primary">
              {t(`PRICE_AND_DELIVERY_TIMES_CHECK`, { ns: 'bearing-hub/bearingHub' })}
            </p>
          </div>
        )}
        <Button
          icon={<Icon size={10} color={IconColor.WHITE} id={IconID.CART} />}
          text={t('BUY_BOX.ADD_TO_CART', { ns: 'product/buyBox' })}
          type={TYPE.PRIMARY}
          onClick={() => {
            CustomEvent.trackEvent(
              IbhEventCategory,
              TrackedIbhEventActions.transferToShoppingBasket,
              JSON.stringify({ article: articleNumber, quantity, price: unitPrice })
            );
            void addToShoppingCart(articleData, articleThumbnail);
          }}
          testId="add-to-cart-button"
          disabled={!priceAvailable || addToBasketInProgress || setupCostIsLoading}
          loading={priceAvailable ? setupCostIsLoading || addToBasketInProgress : false}
          loaderText={t('GENERAL.LOADING', { ns: 'layout/general' })}
        />
        <Button
          icon={<Icon size={10} color={IconColor.WHITE} id={IconID.SHARE} />}
          text={t('BUY_BOX.SHARE_CONFIG', { ns: 'product/buyBox' })}
          type={TYPE.PRIMARY}
          onClick={async () => {
            CustomEvent.trackEvent(IbhEventCategory, TrackedIbhEventActions.share);
            const url = `${window.location.origin}${router.asPath}`;
            await copyLinkToClipboard(url);
          }}
          testId="share_configuration_button"
        />
        <Button
          icon={<Icon size={10} color={IconColor.WHITE} id={IconID.REQUEST_QUOTE} />}
          text={t('BUY_BOX.REQUEST_QUOTE', { ns: 'bearing-hub/bearingHub' })}
          type={TYPE.PRIMARY}
          disabled={addToBasketInProgress || setupCostIsLoading}
          loading={!priceAvailable ? setupCostIsLoading || addToBasketInProgress : false}
          loaderText={t('GENERAL.LOADING', { ns: 'layout/general' })}
          testId="request_quote_button"
          onClick={() => addToShoppingCart(articleData)}
        />
        <ComplianceModal
          visible={!!pendingComplianceArticle}
          onClose={() => setPendingComplianceArticle(null)}
          onConfirm={addPendingComplianceArticleToShoppingCart}
        />
        <Modal
          variant={ModalVariant.ALERT}
          visible={activeModal === BuyBoxModals.ADD_TO_SHOPPING_CART}
          onClose={() => setActiveModal(null)}
          headline={t('FALLBACK_HEADER.SHOPPINGCART', { ns: 'layout/header' })}
        >
          <ShoppingCartModalContent
            onClose={() => setActiveModal(null)}
            mode={ShoppingCartModalContentMode.ADD_ARTICLE}
          />
        </Modal>
        <Modal
          variant={ModalVariant.ALERT}
          visible={activeModal === BuyBoxModals.FILE_CREATION}
          onClose={() => setActiveModal(null)}
          headline={t('FALLBACK_HEADER.FILE_CREATION', { ns: 'layout/header' })}
          closable={false}
        >
          <div>
            <p className="text-base">{t('BUY_BOX.FILE_CREATION_MESSAGE', { ns: 'product/buyBox' })}</p>
            <div className="flex justify-center mt-10">
              <div className="w-20 h-20 rounded-full animate-spin border-solid border-[#F07D00] border-t-transparent border-2" />
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};
